// Фильтр «Ответственный» над таблицей «Контроля» (005: FR-011; сценарий 7.3): запись справочника
// — человек или организация — поиском сервера по ФИО, названию и псевдонимам (directory.search,
// от двух букв, на каждое нажатие с короткой задержкой). Выбор одного значения закрывает панель
// (ux.md, раздел 5); текущее отмечено галочкой; «Все ответственные» снимает условие. ↑ ↓ ходят
// по вариантам, из поля поиска ↓ — на первый. Пункты склеенной записи сервер отдаёт под главной:
// «Горбунов СБ» показывает и пункты, записанные «Горбунов С».
import { DIRECTORY_SEARCH_MAX } from '@kp/contracts';
import { Button, Callout, Icon, Popover, SearchField, cx, useDelayedFlag } from '@kp/ui';
import { Building2, Check, UserRound, Users } from '@kp/ui/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { apiQuery } from '../../api/client.ts';
import { errorView } from '../../app/errors.ts';

const SEARCH_FROM = 2;
const SEARCH_DELAY_MS = 250;
const SEARCH_LIMIT = 10;

export interface ResponsibleFilterProps {
  /** Номер записи справочника из адреса; нет — все ответственные. */
  value: string | undefined;
  /** Имя выбранной записи для кнопки: «Ответственный: Горбунов С. Б.»; не известно — «выбран». */
  valueLabel: string | undefined;
  onChange: (record: { id: string; name: string } | undefined) => void;
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** ↑ ↓ Home End — по вариантам панели (ux.md, раздел 5). */
function moveFocus(event: KeyboardEvent<HTMLElement>, list: HTMLElement | null): void {
  const options = Array.from(list?.querySelectorAll<HTMLButtonElement>('[data-option]') ?? []);
  if (options.length === 0) return;
  const index = options.indexOf(document.activeElement as HTMLButtonElement);
  const target =
    event.key === 'ArrowDown'
      ? options[Math.min(index + 1, options.length - 1)]
      : event.key === 'ArrowUp'
        ? options[Math.max(index - 1, 0)]
        : event.key === 'Home'
          ? options[0]
          : event.key === 'End'
            ? options.at(-1)
            : undefined;
  if (!target) return;
  event.preventDefault();
  target.focus();
}

export function ResponsibleFilter({ value, valueLabel, onChange }: ResponsibleFilterProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const listRef = useRef<HTMLUListElement>(null);
  const trimmed = text.trim();
  const q = useDebounced(trimmed, SEARCH_DELAY_MS);
  const searching = open && q.length >= SEARCH_FROM;
  const search = useQuery({
    ...apiQuery('directory.search', { query: { q, limit: SEARCH_LIMIT } }),
    enabled: searching,
    placeholderData: keepPreviousData,
  });
  const ready = trimmed.length >= SEARCH_FROM;
  const hits = ready ? (search.data?.items ?? []) : [];
  // «Поиск…» — только если ответ дольше 0,3 с (README «Табеля», «Состояния»).
  const slow = useDelayedFlag(ready && (q !== trimmed || search.isFetching) && hits.length === 0);
  const failure = ready && search.isError ? errorView(search.error) : null;

  const choose = (record: { id: string; name: string } | undefined) => {
    onChange(record);
    setOpen(false);
  };

  const shown = value ? (valueLabel ?? 'выбран') : 'все';

  return (
    <Popover
      label="Ответственный"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setText('');
      }}
      className="kp-resp-filter"
      trigger={
        <Button variant="ghost" size="sm" className={cx(value && 'kp-on')}>
          Ответственный: {shown}
        </Button>
      }
    >
      <div
        className="t-col"
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && event.target instanceof HTMLInputElement) {
            event.preventDefault();
            listRef.current?.querySelector<HTMLButtonElement>('[data-option]')?.focus();
            return;
          }
          moveFocus(event, listRef.current);
        }}
      >
        <SearchField
          label="Поиск ответственного по ФИО или названию"
          placeholder="Найти по ФИО или названию"
          maxLength={DIRECTORY_SEARCH_MAX}
          autoFocus
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <ul ref={listRef} className="kp-resp-options" aria-label="Ответственные">
          {value ? (
            <li>
              <button
                type="button"
                data-option=""
                className="kp-resp-option"
                onClick={() => choose(undefined)}
              >
                <Icon icon={Users} />
                <span className="kp-grow">Все ответственные</span>
              </button>
            </li>
          ) : null}
          {hits.map((hit) => (
            <li key={hit.record.id}>
              <button
                type="button"
                data-option=""
                className="kp-resp-option"
                aria-current={hit.record.id === value ? 'true' : undefined}
                onClick={() => choose({ id: hit.record.id, name: hit.record.name })}
              >
                <Icon icon={hit.record.kind === 'person' ? UserRound : Building2} />
                <span className="kp-grow">
                  {hit.record.name}
                  {hit.organization || hit.alias ? (
                    <span className="t-muted">
                      {' '}
                      ·{' '}
                      {[hit.organization?.name, hit.alias ? `«${hit.alias}»` : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : null}
                </span>
                {hit.record.id === value ? <Icon icon={Check} /> : null}
              </button>
            </li>
          ))}
        </ul>
        {failure ? (
          <Callout
            tone="bad"
            role="alert"
            lead={`${failure.message}.`}
            {...(failure.code ? { code: failure.code } : {})}
            {...(failure.operation ? { operation: failure.operation } : {})}
          >
            {failure.nextStep}
          </Callout>
        ) : !ready ? (
          <p className="t-muted kp-resp-hint">Введите ФИО или название — от двух букв</p>
        ) : slow ? (
          <p className="t-muted kp-resp-hint">Поиск…</p>
        ) : search.isSuccess && q === trimmed && hits.length === 0 ? (
          <p className="t-muted kp-resp-hint">Никого не нашлось по «{trimmed}»</p>
        ) : null}
      </div>
    </Popover>
  );
}
